import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Spinner} from '../../Spinner';
import {detectSubdomainFromPublicUrl, extractSubdomainFromInput} from './shopifySubdomain';

/**
 * El campo «URL o Subdominio de Shopify» con sus avisos (detectando, error, subdominio detectado y la ayuda).
 * Es el marcado de siempre de `AddStoreForm`, sacado para que lo use también el pedido del link de
 * instalación: el alta por OAuth lo sigue dibujando idéntico, con su propio estado de clase.
 */
export function ShopifyStoreField({value, onChange, onBlur, isDetecting, detectionError, detectedSubdomain}) {
    const {t} = useTranslation();
    const showDetectedSubdomain = detectedSubdomain && detectedSubdomain !== value;

    return (
        <div className="form-row">
            <div className="form-col col-12">
                <label>{t('add_store_modal.shopify_subdomain_label', 'URL o Subdominio de Shopify')}</label>
                <input name="storeSubdomain" id="storeSubdomain" type="text"
                       value={value || ''}
                       onChange={onChange}
                       onBlur={onBlur}
                       readOnly={false}
                       className="form-control"
                       required="required"
                       placeholder={t('add_store_modal.shopify_input_placeholder', 'xyz, xyz.myshopify.com o www.tutienda.com')}
                />
                {isDetecting && (
                    <div className="field-footnote mt-2 text-info">
                        <Spinner color="positive small" /> {t('add_store_modal.detecting_subdomain', 'Detectando subdominio...')}
                    </div>
                )}
                {detectionError && (
                    <div className="field-footnote mt-2 text-danger">
                        {t('add_store_modal.detection_error', 'Error:')} {detectionError}
                    </div>
                )}
                {showDetectedSubdomain && !isDetecting && (
                    <div className="field-footnote mt-2 text-success">
                        {t('add_store_modal.subdomain_detected', 'Subdominio detectado:')} <strong>{detectedSubdomain}</strong>
                    </div>
                )}
                <div className="field-footnote mt-2">
                    {t('add_store_modal.shopify_input_help', 'Puedes ingresar el subdominio (ej: xyz), la URL completa (ej: xyz.myshopify.com) o la URL pública de tu tienda (ej: www.tutienda.com).')}
                </div>
            </div>
        </div>
    );
}

/**
 * El estado del campo para un componente de función: lo mismo que hace `AddStoreForm` (al salir del campo
 * extrae el subdominio o lo detecta desde la URL pública). `subdomain` es lo que hay para usar: lo detectado
 * o, si no, lo tipeado. Una detección vieja que llega después de que el usuario volvió a escribir se descarta.
 */
export function useShopifyStoreInput(detect = detectSubdomainFromPublicUrl) {
    const [value, setValue] = useState('');
    const [detectedSubdomain, setDetectedSubdomain] = useState(null);
    const [isDetecting, setIsDetecting] = useState(false);
    const [detectionError, setDetectionError] = useState(null);
    const attempt = useRef(0);
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    const onChange = useCallback((event) => {
        attempt.current += 1;
        setValue(event.target.value);
        setDetectedSubdomain(null);
        setIsDetecting(false);
        setDetectionError(null);
    }, []);

    const onBlur = useCallback((event) => {
        const input = event.target.value;
        if (!input || !input.trim()) return;
        const current = attempt.current + 1;
        attempt.current = current;
        const extracted = extractSubdomainFromInput(input);
        if (extracted) {
            setDetectedSubdomain(extracted);
            setIsDetecting(false);
            setDetectionError(null);
            return;
        }
        setIsDetecting(true);
        setDetectionError(null);
        detect(input).then((detected) => {
            if (!mounted.current || attempt.current !== current) return;
            setDetectedSubdomain(detected);
            setIsDetecting(false);
        }, (error) => {
            if (!mounted.current || attempt.current !== current) return;
            setDetectedSubdomain(null);
            setIsDetecting(false);
            setDetectionError((error && error.message) || 'Error al detectar el subdominio');
        });
    }, [detect]);

    return {
        value, onChange, onBlur, isDetecting, detectionError, detectedSubdomain,
        subdomain: (detectedSubdomain || value || '').trim(),
    };
}

export default ShopifyStoreField;
